import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, ArrowLeft, Camera, FileText, Mic, MicOff, Upload, X, Sparkles, AudioLines,
} from "lucide-react";
import { toast } from "sonner";

export default function NewTelematico() {
  const nav = useNavigate();
  const [step, setStep] = useState("form"); // form | adjuntos | procesar
  const [siniestro, setSiniestro] = useState(null);
  const [form, setForm] = useState({ expediente_num: "", asegurado_nombre: "", descripcion: "" });
  const [creating, setCreating] = useState(false);

  const [companies, setCompanies] = useState([]);
  const [policyTemplateId, setPolicyTemplateId] = useState("");

  useEffect(() => {
    api.get("/companies").then((r) => {
      setCompanies(r.data?.items || []);
    }).catch(() => {});
  }, []);

  const santander = companies.find((c) => c.slug === "santander");
  const policyTemplates = santander?.policy_templates || [];
  // Orden alfabético español memoizado.
  const policyTemplatesSorted = useMemo(
    () => [...policyTemplates].sort((a, b) => (a.name || "").localeCompare(b.name || "", "es", { sensitivity: "base" })),
    [policyTemplates]
  );

  // adjuntos
  const [photos, setPhotos] = useState([]);
  const [docs, setDocs] = useState([]);
  const photoInput = useRef(null);
  const docInput = useRef(null);
  const [uploading, setUploading] = useState(false);

  // audio
  const [recording, setRecording] = useState(false);
  const [audioTranscribing, setAudioTranscribing] = useState(false);
  const [audioText, setAudioText] = useState("");
  const recorderRef = useRef(null);
  const audioStreamRef = useRef(null);

  // texto manual
  const [manualText, setManualText] = useState("");
  const [manualSaving, setManualSaving] = useState(false);

  // procesado IA
  const [processing, setProcessing] = useState(false);

  const createSiniestro = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { ...form };
      if (policyTemplateId) payload.policy_template_id = policyTemplateId;
      const r = await api.post("/siniestros/telematico", payload);
      setSiniestro(r.data);
      setStep("adjuntos");
      toast.success(`Expediente creado: ${r.data.expediente_num}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear el expediente");
    } finally {
      setCreating(false);
    }
  };

  const onPhotoFiles = async (files) => {
    if (!files?.length || !siniestro) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post(`/siniestros/${siniestro.id}/upload-photo`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((prev) => [...prev, { id: r.data.id, filename: r.data.filename, size_bytes: r.data.size_bytes }]);
      }
      toast.success(`${files.length} foto(s) subida(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir foto");
    } finally {
      setUploading(false);
    }
  };

  const onDocFiles = async (files) => {
    if (!files?.length || !siniestro) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post(`/siniestros/${siniestro.id}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setDocs((prev) => [...prev, r.data]);
      }
      toast.success(`${files.length} documento(s) subido(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir documento");
    } finally {
      setUploading(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      const chunks = [];
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      rec.ondataavailable = (e) => { if (e.data?.size > 0) chunks.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        audioStreamRef.current = null;
        setAudioTranscribing(true);
        try {
          const blob = new Blob(chunks, { type: "audio/webm" });
          const fd = new FormData();
          fd.append("file", blob, "explicacion.webm");
          fd.append("role", "tramitador");
          const r = await api.post(`/siniestros/${siniestro.id}/transcribe-file`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120_000,
          });
          if (r.data?.text) {
            setAudioText(r.data.text);
            toast.success("Audio transcrito");
          } else {
            toast("Audio sin contenido reconocible");
          }
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Error al transcribir");
        } finally {
          setAudioTranscribing(false);
        }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (err) {
      toast.error("No se pudo acceder al micrófono");
    }
  };

  const stopRecording = () => {
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
    setRecording(false);
  };

  const saveManualText = async () => {
    if (!siniestro) return;
    setManualSaving(true);
    try {
      await api.post(`/siniestros/${siniestro.id}/manual-transcript`, { text: manualText });
      toast.success(manualText.trim() ? "Redacción guardada" : "Redacción borrada");
    } catch (err) {
      toast.error("No se pudo guardar el texto");
    } finally {
      setManualSaving(false);
    }
  };

  const processAI = async () => {
    if (!siniestro) return;
    if (manualText.trim()) await saveManualText();
    setProcessing(true);
    // Guardamos un baseline del `updated_at` actual para detectar después si la
    // IA terminó aunque el cliente perdiera la respuesta (504 del ingress).
    let baselineUpdatedAt = null;
    try {
      const before = await api.get(`/siniestros/${siniestro.id}/ai-notes`).catch(() => null);
      baselineUpdatedAt = before?.data?.updated_at || before?.data?.valoracion_updated_at || null;
    } catch (e) { /* sin baseline, seguimos */ }

    try {
      await api.post(`/siniestros/${siniestro.id}/ai-report`, null, { timeout: 180_000 });
      toast.success("IA ha procesado el expediente. Revisa la valoración.");
      nav(`/siniestros/${siniestro.id}`);
      return;
    } catch (err) {
      // El procesado IA tarda 30-90s habitualmente. Si el ingress corta a 60s
      // o el cliente cancela por timeout, el backend SÍ termina el trabajo.
      // Antes de mostrar un error, comprobamos durante hasta 3 minutos si
      // realmente terminó (poll a /ai-notes cada 5s).
      const isTimeoutOrGateway =
        err?.code === "ECONNABORTED" ||
        !err?.response ||
        [502, 503, 504].includes(err?.response?.status);
      if (isTimeoutOrGateway) {
        toast.message("El procesado está tardando más de lo habitual, espera unos segundos…");
        for (let i = 0; i < 24; i++) { // 24 × 5s = 2 min
          await new Promise(r => setTimeout(r, 5000));
          try {
            const r = await api.get(`/siniestros/${siniestro.id}/ai-notes`);
            const updated = r.data?.updated_at || r.data?.valoracion_updated_at;
            const hasContent = !!(r.data?.narrative || r.data?.valoracion);
            // Si hay narrativa/valoración nueva (o el updated_at cambió respecto al
            // baseline), el procesado terminó aunque no recibiéramos la respuesta.
            if (hasContent && updated !== baselineUpdatedAt) {
              toast.success("IA ha procesado el expediente. Revisa la valoración.");
              nav(`/siniestros/${siniestro.id}`);
              return;
            }
          } catch (e) { /* reintentamos */ }
        }
        toast.error("La IA está tardando más de lo esperado. Vuelve a la bandeja en unos minutos y revisa el expediente.");
      } else {
        toast.error(err?.response?.data?.detail || "Error al procesar con IA");
      }
    } finally {
      setProcessing(false);
    }
  };

  const removePhoto = async (id) => {
    try {
      await api.delete(`/photos/${id}`);
      setPhotos((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };
  const removeDoc = async (id) => {
    try {
      await api.delete(`/siniestros/${siniestro.id}/documents/${id}`);
      setDocs((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  // cleanup
  useEffect(() => {
    return () => {
      const s = audioStreamRef.current;
      if (s) s.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <div className="max-w-3xl mx-auto" data-testid="new-telematico-page">
      <Link to="/" className="text-sm text-[#7A786F] hover:text-[#2C2C2A] inline-flex items-center mb-3">
        <ArrowLeft className="h-4 w-4 mr-1" /> Volver
      </Link>
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-[#2C2C2A]">Nuevo Telemático</h1>
          <p className="text-[#7A786F] mt-1 text-sm">
            Expediente <strong>sin videollamada</strong>, para tramitación a distancia. Compañía fija: <strong>Santander</strong> (se aplicará su baremo).
          </p>
        </div>
      </div>

      {step === "form" && (
        <form onSubmit={createSiniestro} className="bg-white border border-[#E5E2D8] rounded-[14px] p-7 mt-6 space-y-5" data-testid="telematico-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="exp">Expediente *</Label>
              <Input id="exp" required value={form.expediente_num}
                onChange={(e) => setForm({ ...form, expediente_num: e.target.value })}
                placeholder="EXP-2026-00123" data-testid="tel-exp-num-input"
                className="rounded-[12px] border-[#E5E2D8]" />
            </div>
            <div>
              <Label htmlFor="nom">Nombre del asegurado *</Label>
              <Input id="nom" required value={form.asegurado_nombre}
                onChange={(e) => setForm({ ...form, asegurado_nombre: e.target.value })}
                placeholder="María García" data-testid="tel-asegurado-input"
                className="rounded-[12px] border-[#E5E2D8]" />
            </div>
          </div>
          <div>
            <Label htmlFor="desc">Descripción inicial</Label>
            <Textarea id="desc" rows={4} value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              placeholder="Daños observados, vehículo, dirección, qué llegó del cliente…"
              data-testid="tel-descripcion-input"
              className="rounded-[12px] border-[#E5E2D8]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label>Compañía aseguradora</Label>
              <div className="rounded-[12px] border border-[#E5E2D8] bg-[#FBFAF6] px-3 py-2 text-sm text-[#2C2C2A]" data-testid="tel-company-fixed">
                Santander <span className="text-[11px] text-[#7A786F]">· compañía fija para expedientes telemáticos</span>
              </div>
            </div>
            {policyTemplates.length > 0 && (
              <div>
                <Label htmlFor="tel-policy">Modalidad de póliza suscrita</Label>
                <Select value={policyTemplateId || "__none__"} onValueChange={(v) => setPolicyTemplateId(v === "__none__" ? "" : v)}>
                  <SelectTrigger id="tel-policy" data-testid="tel-policy-template-select" className="rounded-[12px] border-[#E5E2D8]"><SelectValue placeholder="Sin especificar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin especificar</SelectItem>
                    {policyTemplatesSorted.map((t) => (
                      <SelectItem key={t.id} value={t.id} data-testid={`tel-policy-template-option-${t.id}`}>{t.name}</SelectItem>
                    ))}
                    <SelectItem value="otra">Otra póliza (criterios genéricos)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-[#7A786F] mt-1">La IA consultará el condicionado de esta modalidad.</p>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button type="button" variant="outline" className="border-[#E5E2D8] rounded-[12px]" onClick={() => nav(-1)}>Cancelar</Button>
            <Button type="submit" disabled={creating} className="btn-eks-primary rounded-[12px]" data-testid="tel-create-submit">
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Crear expediente
            </Button>
          </div>
        </form>
      )}

      {step === "adjuntos" && siniestro && (
        <div className="space-y-6 mt-6">
          {/* Fotografías */}
          <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-6" data-testid="tel-photos-block">
            <div className="flex items-center gap-2 mb-3">
              <Camera className="h-5 w-5 text-[#D85A30]" />
              <div className="font-medium text-[#2C2C2A]">Fotografías</div>
              <span className="text-xs text-[#7A786F]">({photos.length} subidas)</span>
            </div>
            <input ref={photoInput} type="file" accept="image/*" multiple className="hidden"
              onChange={(e) => { onPhotoFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
              data-testid="tel-photo-input"
            />
            <Button variant="outline" size="sm" onClick={() => photoInput.current?.click()} disabled={uploading} className="rounded-[10px] border-[#E5E2D8]" data-testid="tel-add-photos-btn">
              <Upload className="h-4 w-4 mr-2" /> Añadir fotos
            </Button>
            {photos.length > 0 && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {photos.map((ph) => (
                  <div key={ph.id} className="flex items-center gap-2 bg-[#FBFAF6] border border-[#E5E2D8] rounded-[10px] p-2 text-sm" data-testid={`tel-photo-${ph.id}`}>
                    <Camera className="h-4 w-4 text-[#7A786F]" />
                    <span className="flex-1 truncate">{ph.filename}</span>
                    <button onClick={() => removePhoto(ph.id)} className="text-[#7A786F] hover:text-[#B43F3F] p-1"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Documentación */}
          <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-6" data-testid="tel-docs-block">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-5 w-5 text-[#D85A30]" />
              <div className="font-medium text-[#2C2C2A]">Documentación</div>
              <span className="text-xs text-[#7A786F]">({docs.length} subidos)</span>
            </div>
            <input ref={docInput} type="file" accept=".pdf,.doc,.docx,.txt,application/pdf" multiple className="hidden"
              onChange={(e) => { onDocFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
              data-testid="tel-doc-input"
            />
            <Button variant="outline" size="sm" onClick={() => docInput.current?.click()} disabled={uploading} className="rounded-[10px] border-[#E5E2D8]" data-testid="tel-add-docs-btn">
              <Upload className="h-4 w-4 mr-2" /> Añadir documentos
            </Button>
            {docs.length > 0 && (
              <div className="mt-3 space-y-1.5">
                {docs.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 bg-[#FBFAF6] border border-[#E5E2D8] rounded-[10px] p-2 text-sm" data-testid={`tel-doc-${d.id}`}>
                    <FileText className="h-4 w-4 text-[#7A786F]" />
                    <span className="flex-1 truncate">{d.filename}</span>
                    <span className="text-[11px] text-[#7A786F] font-mono">{Math.round(d.size_bytes / 1024)} KB</span>
                    <button onClick={() => removeDoc(d.id)} className="text-[#7A786F] hover:text-[#B43F3F] p-1"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Audio o texto */}
          <div className="bg-white border border-[#E5E2D8] rounded-[14px] p-6" data-testid="tel-audio-block">
            <div className="flex items-center gap-2 mb-3">
              <AudioLines className="h-5 w-5 text-[#D85A30]" />
              <div className="font-medium text-[#2C2C2A]">Explicación del tramitador</div>
            </div>
            <p className="text-xs text-[#7A786F] mb-3">Graba un audio explicando el siniestro <em>o</em> escribe una redacción libre. La IA usará este texto junto con las fotos y documentos.</p>

            <div className="flex items-center gap-3 mb-4">
              {!recording ? (
                <Button onClick={startRecording} disabled={audioTranscribing} variant="outline" className="rounded-[10px] border-[#E5E2D8]" data-testid="tel-record-start-btn">
                  <Mic className="h-4 w-4 mr-2 text-[#1D9E75]" /> Grabar audio
                </Button>
              ) : (
                <Button onClick={stopRecording} variant="outline" className="rounded-[10px] border-[#B43F3F] text-[#B43F3F]" data-testid="tel-record-stop-btn">
                  <MicOff className="h-4 w-4 mr-2" /> Detener grabación
                </Button>
              )}
              {audioTranscribing && <span className="text-sm text-[#7A786F] inline-flex items-center"><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Transcribiendo…</span>}
            </div>

            {audioText && (
              <div className="bg-[#E6F5EF] border border-[#A6E3CB] rounded-[10px] p-3 text-sm text-[#2C2C2A] mb-4" data-testid="tel-audio-transcript">
                <div className="font-medium text-[#1D9E75] text-xs uppercase tracking-wide mb-1">Audio transcrito</div>
                {audioText}
              </div>
            )}

            <Label htmlFor="manual" className="block mb-1">Redacción libre (opcional)</Label>
            <Textarea
              id="manual"
              rows={6}
              value={manualText}
              onChange={(e) => setManualText(e.target.value)}
              onBlur={saveManualText}
              placeholder="Describe causa, daños observados, intervenciones urgentes, contexto que no esté en las fotos…"
              className="rounded-[12px] border-[#E5E2D8]"
              data-testid="tel-manual-text"
            />
            {manualSaving && <p className="text-[11px] text-[#7A786F] mt-1"><Loader2 className="h-3 w-3 inline animate-spin mr-1" /> Guardando…</p>}
          </div>

          {/* Procesar con IA */}
          <div className="bg-[#FAECE7] border border-[#F5C2B3] rounded-[14px] p-5 flex items-center gap-4" data-testid="tel-process-block">
            <Sparkles className="h-6 w-6 text-[#D85A30] shrink-0" />
            <div className="flex-1">
              <div className="font-medium text-[#2C2C2A]">Procesar con IA</div>
              <p className="text-xs text-[#7A786F] leading-snug">
                Se transcribirá el audio si lo hay, se redactarán &quot;Causas y circunstancias&quot; y se generará la valoración aplicando el <strong>baremo Santander</strong>. Podrás editarlo todo después.
              </p>
            </div>
            <Button onClick={processAI} disabled={processing} className="btn-eks-primary rounded-[12px] shrink-0" data-testid="tel-process-ai-btn">
              {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Procesar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
